// src/components/messaging/MessageThread.jsx
import React, { useMemo, memo, useEffect, useState, useRef, useCallback } from 'react';
import { getUserInfo } from '../../services/enhancedFirestore';
import { throttle } from '../../utils/performance';
import '../../styles/components/messaging.css';

// OPTIMIZATION: Extracted into separate component for better reusability and memoization
const MessageBubble = memo(({ message, isCurrentUser, showTime, time, senderName, participantNames }) => {
  // Logic to determine sender name with fallbacks
  const getSenderDisplayName = () => {
    // Don't show name if it's the current user
    if (isCurrentUser) return null;
    
    // If we have a directly provided name, use it
    if (senderName) return senderName;
    
    // Try the senderName from the message object
    if (message.senderName) return message.senderName;
    
    // Try from participantNames map
    if (participantNames && message.senderId && participantNames[message.senderId]) {
      return participantNames[message.senderId];
    }
    
    // Try from message.conversationMembers
    if (message.conversationMembers && message.senderId && 
        message.conversationMembers[message.senderId]?.displayName) {
      return message.conversationMembers[message.senderId].displayName;
    }
    
    // Default fallback - should rarely get here with all the checks above
    return message.senderId || 'Unknown';
  };
  
  const displayName = getSenderDisplayName();
  
  // Add class for optimistic messages to show visual feedback
  const messageClass = `message ${isCurrentUser ? 'outgoing' : 'incoming'} ${message.isOptimistic ? 'optimistic' : ''}`;
  
  return (
    <div className={messageClass}>
      {!isCurrentUser && displayName && (
        <div className="message-sender">{displayName}</div>
      )}
      <div className="message-content">
        <div className="message-text">{message.text}</div>
        {showTime && (
          <div className="message-time">
            {message.isOptimistic ? 'Sending...' : time}
          </div>
        )}
      </div>
    </div>
  );
});

// OPTIMIZATION: Extracted into separate component for better reusability and memoization
const MessageGroup = memo(({ messages, senderId, currentUserId, formatMessageTime, senderDisplayName, participantNames }) => {
  const isOutgoing = senderId === currentUserId;
  
  return (
    <div className={`message-group ${isOutgoing ? 'outgoing' : 'incoming'}`}>
      {messages.map((message, index) => {
        // OPTIMIZATION: Only show time for the last message in a group
        const showTime = index === messages.length - 1;
        const time = formatMessageTime(message.createdAt);
        
        return (
          <MessageBubble 
            key={message.id || index}
            message={message}
            isCurrentUser={isOutgoing}
            showTime={showTime}
            time={time}
            senderName={senderDisplayName}
            participantNames={participantNames}
          />
        );
      })}
    </div>
  );
});

// OPTIMIZATION: Extracted date separator into its own component
const DateSeparator = memo(({ date }) => (
  <div className="date-separator">
    <span>{date}</span>
  </div>
));

const MessageThread = ({ messages, currentUserId, activeConversation }) => {
  const threadRef = useRef(null);
  const [participantNames, setParticipantNames] = useState({});
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [isNearBottom, setIsNearBottom] = useState(true);

  // Load participant names for the conversation
  useEffect(() => {
    const loadParticipantNames = async () => {
      if (!activeConversation || !activeConversation.participants) {
        return;
      }
      
      // Create a map to store names
      const names = {};
      
      // First try to get names from the conversation object
      if (activeConversation.members) {
        Object.keys(activeConversation.members).forEach(userId => {
          if (activeConversation.members[userId]?.displayName) {
            names[userId] = activeConversation.members[userId].displayName;
          }
        });
      }
      
      // For any missing names, try to look them up
      const missingIds = activeConversation.participants.filter(id => !names[id]);
      
      for (const userId of missingIds) {
        try {
          const userInfo = await getUserInfo(userId);
          if (userInfo && userInfo.displayName) {
            names[userId] = userInfo.displayName;
          } else {
            // Set a default if we can't find the name
            names[userId] = userId === currentUserId ? "You" : "Unknown";
          }
        } catch (error) {
          console.error(`Error fetching name for user ${userId}:`, error);
          names[userId] = "Unknown";
        }
      }
      
      console.log("Loaded participant names:", names);
      setParticipantNames(names);
    };
    
    loadParticipantNames();
  }, [activeConversation, currentUserId]);
  
  // OPTIMIZATION: Memoize utility functions to prevent recreating on every render
  const formatMessageTime = useMemo(() => {
    return (timestamp) => {
      if (!timestamp) return '';
      
      const date = timestamp instanceof Date 
        ? timestamp 
        : new Date(timestamp.seconds ? timestamp.seconds * 1000 : timestamp);
      
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
  }, []);
  
  const formatDate = useMemo(() => {
    return (dateObj) => {
      if (!dateObj || !(dateObj instanceof Date)) return '';
      const today = new Date();
      const yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      // Compare only the date parts (year, month, day)
      const isToday = dateObj.getFullYear() === today.getFullYear() &&
        dateObj.getMonth() === today.getMonth() &&
        dateObj.getDate() === today.getDate();
      const isYesterday = dateObj.getFullYear() === yesterday.getFullYear() &&
        dateObj.getMonth() === yesterday.getMonth() &&
        dateObj.getDate() === yesterday.getDate();
      if (isToday) {
        return 'Today';
      } else if (isYesterday) {
        return 'Yesterday';
      } else {
        return dateObj.toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          year: 'numeric'
        });
      }
    };
  }, []);
  
  // OPTIMIZATION: Memoize the grouping functions to prevent recalculation on every render
  
  // Group messages by date
  const groupMessagesByDate = useMemo(() => {
    const groups = [];
    let currentDateKey = null;
    let currentDateObj = null;
    let currentGroup = [];

    messages.forEach(message => {
      const messageDate = message.createdAt
        ? new Date(message.createdAt.seconds ? message.createdAt.seconds * 1000 : message.createdAt)
        : new Date();
      // Use only the date part for grouping
      const dateKey = `${messageDate.getFullYear()}-${messageDate.getMonth()}-${messageDate.getDate()}`;

      if (dateKey !== currentDateKey) {
        if (currentGroup.length > 0) {
          groups.push({
            date: currentDateObj, // Store as Date object
            messages: currentGroup
          });
        }
        currentDateKey = dateKey;
        currentDateObj = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
        currentGroup = [message];
      } else {
        currentGroup.push(message);
      }
    });

    if (currentGroup.length > 0) {
      groups.push({
        date: currentDateObj,
        messages: currentGroup
      });
    }

    return groups;
  }, [messages]);
  
  // Group consecutive messages by sender
  const groupConsecutiveMessages = useMemo(() => {
    return (messagesList) => {
      const groups = [];
      let currentGroup = null;
      
      messagesList.forEach(message => {
        // Start a new group if:
        // 1. There is no current group
        // 2. The sender is different from the current group
        // 3. Time gap is more than 5 minutes
        const messageTime = message.createdAt 
          ? new Date(message.createdAt.seconds ? message.createdAt.seconds * 1000 : message.createdAt) 
          : new Date();
        
        const shouldStartNewGroup = 
          !currentGroup || 
          currentGroup.senderId !== message.senderId ||
          (messageTime - new Date(currentGroup.lastTimestamp)) > 5 * 60 * 1000;
        
        if (shouldStartNewGroup) {
          if (currentGroup) {
            groups.push(currentGroup);
          }
          
          currentGroup = {
            senderId: message.senderId,
            messages: [message],
            lastTimestamp: messageTime
          };
        } else {
          // Add to current group
          currentGroup.messages.push(message);
          currentGroup.lastTimestamp = messageTime;
        }
      });
      
      // Add the last group
      if (currentGroup) {
        groups.push(currentGroup);
      }
      
      return groups;
    };
  }, []);
  
  // Throttled scroll handler
  const handleScroll = useMemo(
    () => throttle(() => {
      if (threadRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = threadRef.current;
        const nearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setIsNearBottom(nearBottom);
      }
    }, 100),
    []
  );

  // Scroll to bottom when messages change (with throttling)
  useEffect(() => {
    const scrollToBottom = () => {
      if (threadRef.current && isNearBottom) {
        const { scrollHeight } = threadRef.current;
        threadRef.current.scrollTop = scrollHeight;
      }
    };

    // Use requestAnimationFrame for smooth scrolling
    requestAnimationFrame(scrollToBottom);
  }, [messages?.length, isNearBottom]); // Only depend on messages length to avoid unnecessary updates

  // Add scroll listener
  useEffect(() => {
    const currentRef = threadRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  // OPTIMIZATION: Render empty state only once when necessary
  if (messages.length === 0) {
    return (
      <div className="message-thread">
        <div className="empty-thread">
          <p>No messages yet. Start the conversation by typing a message below.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      ref={threadRef} 
      className="message-thread"
      style={{
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        willChange: 'scroll-position'
      }}
    >
      {groupMessagesByDate.map((group, groupIndex) => (
        <div key={groupIndex} className="message-date-group">
          <DateSeparator date={formatDate(group.date)} />
          
          {groupConsecutiveMessages(group.messages).map((messageGroup, messageGroupIndex) => {
            // Get the sender display name for this group
            const senderDisplayName = participantNames[messageGroup.senderId] || 'Unknown';
            
            return (
              <MessageGroup 
                key={`group-${groupIndex}-${messageGroup.senderId}`}
                messages={messageGroup.messages}
                senderId={messageGroup.senderId}
                currentUserId={currentUserId}
                formatMessageTime={formatMessageTime}
                senderDisplayName={senderDisplayName}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
};

// OPTIMIZATION: Use React.memo to prevent unnecessary renders
export default memo(MessageThread);